import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, List, Plus, Target } from 'lucide-react';
import {
  LEAD_STATUS, LEAD_TRANSITIONS, PRIORITY, dateLabel, dateTimeLabel, type LeadStatus, type Priority,
} from '@adisys/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { MetaBadge, errorText, inr, useCrmMeta } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { LogoField } from '../../components/LogoField';
import { Column, DataTable, FilterBar, FilterSelect, SearchInput } from '../../components/DataTable';
import {
  Button, Card, EmptyState, ErrorState, Field, Input, Modal, Select, Skeleton, StatusBadge, Textarea, cx, useToast,
} from '../../components/ui';

/* ===================================================================
   Shared pieces (also used by LeadDetail)
   =================================================================== */

/** Moves a lead between statuses. "Lost" needs a reason, so it opens a small modal first. */
export function useLeadStatusChange(onDone?: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<{ lead: any; status: LeadStatus } | null>(null);
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: (v: { id: string; status: LeadStatus; lostReason?: string }) =>
      api.post(`/crm/leads/${v.id}/status`, { status: v.status, lostReason: v.lostReason }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['crm', 'leads'] });
      void qc.invalidateQueries({ queryKey: ['crm', 'dashboard'] });
      toast.success(`Marked ${LEAD_STATUS.byValue[v.status].label.toLowerCase()}`);
      setPending(null); setReason('');
      onDone?.();
    },
    onError: (e) => toast.error('Could not change the status', errorText(e)),
  });

  const request = (lead: any, status: LeadStatus) => {
    if (status === 'lost') { setPending({ lead, status }); return; }
    mutation.mutate({ id: lead.id, status });
  };

  const modal = (
    <Modal open={!!pending} onClose={() => setPending(null)} title="Mark enquiry as lost"
      description={pending ? `${pending.lead.company} — say why, so the pipeline report stays honest.` : undefined}
      size="sm"
      footer={<>
        <Button onClick={() => setPending(null)}>Cancel</Button>
        <Button variant="danger" loading={mutation.isPending} disabled={reason.trim().length < 3}
          onClick={() => pending && mutation.mutate({ id: pending.lead.id, status: 'lost', lostReason: reason.trim() })}>
          Mark as lost
        </Button>
      </>}>
      <Field label="Reason" required>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Went with another vendor on price" />
      </Field>
    </Modal>
  );
  return { request, modal, busy: mutation.isPending };
}

/** Buttons for every status the lead may legally move to. */
export function StatusButtons({ lead, onRequest, busy }: { lead: any; onRequest: (l: any, s: LeadStatus) => void; busy?: boolean }) {
  const next = LEAD_TRANSITIONS[lead.status as LeadStatus] ?? [];
  if (!next.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {next.map((s) => (
        <Button key={s} size="sm" disabled={busy} variant={s === 'lost' ? 'ghost' : 'secondary'} onClick={() => onRequest(lead, s)}>
          {s === 'new' ? 'Reopen' : `Mark ${LEAD_STATUS.byValue[s].label.toLowerCase()}`}
        </Button>
      ))}
    </div>
  );
}

const EMPTY_FORM = {
  company: '', contactPerson: '', mobile: '', email: '', location: '', industry: '', projectType: '',
  solutions: [] as string[], requirementSummary: '', budget: '', source: '', priority: 'medium',
  expectedCloseDate: '', assignedTo: '',
};
/** undefined = leave the saved logo alone; null = remove it. */
type LogoState = string | null | undefined;

/** Create or edit an enquiry. Pass `lead` to edit. */
export function LeadFormModal({ open, onClose, lead }: { open: boolean; onClose: () => void; lead?: any }) {
  const { user, can } = useAuth();
  const meta = useCrmMeta();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<ApiRequestError | null>(null);
  const [logo, setLogo] = useState<LogoState>(undefined);

  // Re-seed the form each time it opens (for a different lead, or fresh).
  const key = open ? (lead?.id ?? 'new') : null;
  if (key !== loadedFor) {
    setLoadedFor(key);
    setError(null);
    setLogo(undefined);
    setForm(lead ? {
      company: lead.company, contactPerson: lead.contactPerson, mobile: lead.mobile, email: lead.email ?? '',
      location: lead.location, industry: lead.industry ?? '', projectType: lead.projectType ?? '',
      solutions: lead.solutions ?? [], requirementSummary: lead.requirementSummary ?? '',
      budget: lead.budget === null || lead.budget === undefined ? '' : String(lead.budget), source: lead.source ?? '',
      priority: lead.priority, expectedCloseDate: lead.expectedCloseDate ?? '', assignedTo: lead.assignedTo ?? '',
    } : { ...EMPTY_FORM, assignedTo: user?.id ?? '' });
  }

  const set = (k: keyof typeof EMPTY_FORM, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const allCanAssign = can('crm.lead.view.all');

  const save = useMutation({
    mutationFn: () => {
      const body: any = {
        company: form.company, contactPerson: form.contactPerson, mobile: form.mobile,
        email: form.email || null, location: form.location, industry: form.industry || null,
        projectType: form.projectType || null, solutions: form.solutions,
        requirementSummary: form.requirementSummary || null,
        budget: form.budget === '' ? null : Number(form.budget), source: form.source || null,
        priority: form.priority, expectedCloseDate: form.expectedCloseDate || null,
      };
      if (allCanAssign && form.assignedTo) body.assignedTo = form.assignedTo;
      if (logo !== undefined) body.logo = logo;
      return lead ? api.patch(`/crm/leads/${lead.id}`, body) : api.post('/crm/leads', body);
    },
    onSuccess: (res: any) => {
      void qc.invalidateQueries({ queryKey: ['crm', 'leads'] });
      void qc.invalidateQueries({ queryKey: ['crm', 'dashboard'] });
      toast.success(lead ? 'Enquiry updated' : 'Enquiry created');
      onClose();
      if (!lead && res?.id) navigate(`/crm/leads/${res.id}`);
    },
    onError: (e) => { if (e instanceof ApiRequestError) setError(e); else toast.error('Could not save', errorText(e)); },
  });

  const fe = (f: string) => error?.fieldError(f);
  const toggleSolution = (name: string) =>
    set('solutions', form.solutions.includes(name) ? form.solutions.filter((s) => s !== name) : [...form.solutions, name]);

  return (
    <Modal open={open} onClose={onClose} size="lg" title={lead ? `Edit ${lead.leadCode}` : 'New enquiry'}
      description="Capture who is asking, where, and what they need."
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => { setError(null); save.mutate(); }}>
          {lead ? 'Save changes' : 'Create enquiry'}
        </Button>
      </>}>
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); setError(null); save.mutate(); }}>
        {error && !error.details && <p className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger sm:col-span-2">{error.message}</p>}
        <Field label="Customer company name" required error={fe('company')} hint="Printed on the proposal cover and signature block."><Input value={form.company} invalid={!!fe('company')} onChange={(e) => set('company', e.target.value)} /></Field>
        <Field label="Customer logo" error={fe('logo')} hint="Optional. Shown on the proposal; PNG, JPEG or WebP.">
          <LogoField value={logo === undefined ? (lead?.logo ?? null) : logo} onChange={setLogo} />
        </Field>
        <Field label="Contact person" required error={fe('contactPerson')}><Input value={form.contactPerson} invalid={!!fe('contactPerson')} onChange={(e) => set('contactPerson', e.target.value)} /></Field>
        <Field label="Mobile" required error={fe('mobile')}><Input inputMode="tel" value={form.mobile} invalid={!!fe('mobile')} onChange={(e) => set('mobile', e.target.value)} /></Field>
        <Field label="Email" error={fe('email')}><Input type="email" value={form.email} invalid={!!fe('email')} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field label="Location" required error={fe('location')} className="sm:col-span-2" hint="Site address or city">
          <Input value={form.location} invalid={!!fe('location')} onChange={(e) => set('location', e.target.value)} />
        </Field>
        <Field label="Industry" error={fe('industry')}>
          <Select value={form.industry} onChange={(e) => set('industry', e.target.value)}>
            <option value="">Select…</option>
            {(meta.data?.industries ?? []).map((i: string) => <option key={i}>{i}</option>)}
          </Select>
        </Field>
        <Field label="Project type" error={fe('projectType')}>
          <Select value={form.projectType} onChange={(e) => set('projectType', e.target.value)}>
            <option value="">Select…</option>
            {(meta.data?.projectTypes ?? []).map((i: string) => <option key={i}>{i}</option>)}
          </Select>
        </Field>

        <fieldset className="sm:col-span-2">
          <legend className="mb-1.5 text-xs font-medium text-ink-700">Solutions required</legend>
          <div className="flex flex-wrap gap-2">
            {(meta.data?.solutions ?? []).map((s: any) => {
              const on = form.solutions.includes(s.name);
              return (
                <button key={s.id} type="button" aria-pressed={on} onClick={() => toggleSolution(s.name)}
                  className={cx('rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
                    on ? 'bg-brand-500 text-white ring-brand-500' : 'bg-white text-ink-700 ring-line-strong hover:bg-ink-50')}>
                  {s.name}
                </button>
              );
            })}
          </div>
          {fe('solutions') && <p className="mt-1 text-xs text-danger">{fe('solutions')}</p>}
        </fieldset>

        <Field label="Requirement summary" className="sm:col-span-2" error={fe('requirementSummary')}>
          <Textarea rows={3} value={form.requirementSummary} onChange={(e) => set('requirementSummary', e.target.value)} />
        </Field>
        <Field label="Project budget (₹)" error={fe('budget')}><Input type="number" min={0} value={form.budget} invalid={!!fe('budget')} onChange={(e) => set('budget', e.target.value)} /></Field>
        <Field label="Source" error={fe('source')}>
          <Select value={form.source} onChange={(e) => set('source', e.target.value)}>
            <option value="">Select…</option>
            {(meta.data?.sources ?? []).map((i: string) => <option key={i}>{i}</option>)}
          </Select>
        </Field>
        <Field label="Priority">
          <Select value={form.priority} onChange={(e) => set('priority', e.target.value)}>
            {PRIORITY.list.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
        </Field>
        <Field label="Expected closing date" error={fe('expectedCloseDate')}>
          <Input type="date" value={form.expectedCloseDate} invalid={!!fe('expectedCloseDate')} onChange={(e) => set('expectedCloseDate', e.target.value)} />
        </Field>
        <Field label="Sales executive" className="sm:col-span-2" error={fe('assignedTo')}
          hint={allCanAssign ? undefined : 'Enquiries you create are assigned to you.'}>
          <Select value={form.assignedTo} disabled={!allCanAssign} onChange={(e) => set('assignedTo', e.target.value)}>
            {!allCanAssign && user && <option value={user.id}>{user.fullName}</option>}
            {allCanAssign && (meta.data?.people?.sales ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.fullName}</option>)}
          </Select>
        </Field>
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}

/* ===================================================================
   Page
   =================================================================== */
type SortKey = 'createdAt' | 'expectedCloseDate' | 'budget' | 'company' | 'priority';

export function LeadsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const meta = useCrmMeta();
  const [params] = useSearchParams();

  const [view, setView] = useState<'list' | 'board'>('list');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(params.get('status') ?? '');
  const [priority, setPriority] = useState('');
  const [source, setSource] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [followUp, setFollowUp] = useState(params.get('followUp') ?? '');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'createdAt', dir: 'desc' });
  const [createOpen, setCreateOpen] = useState(false);
  const statusChange = useLeadStatusChange();

  const size = view === 'board' ? 200 : 25;
  const query = useQuery({
    queryKey: ['crm', 'leads', { search, status, priority, source, assignedTo, followUp, page, sort, size }],
    queryFn: () => api.get('/crm/leads', {
      q: search || undefined, status: status || undefined, priority: priority || undefined, source: source || undefined,
      assignedTo: assignedTo || undefined, followUp: followUp || undefined,
      page: view === 'board' ? 1 : page, size, sort: sort.key, dir: sort.dir,
    }),
  });
  const rows: any[] = query.data?.data ?? [];
  const totals = query.data?.totals;
  const activeFilters = [search, status, priority, source, assignedTo, followUp].filter(Boolean).length;
  const reset = () => { setSearch(''); setStatus(''); setPriority(''); setSource(''); setAssignedTo(''); setFollowUp(''); setPage(1); };
  const reflow = (fn: () => void) => { fn(); setPage(1); };

  const columns: Array<Column<any>> = [
    { key: 'company', header: 'Enquiry', sortable: true, render: (l) => (
      <div className="min-w-0">
        <p className="truncate font-medium text-ink-900">{l.company}</p>
        <p className="tabular truncate text-xs text-ink-500">{l.leadCode} · {l.contactPerson} · {l.mobile}</p>
      </div>) },
    { key: 'location', header: 'Location', hideBelow: 'lg', render: (l) => <span className="line-clamp-1">{l.location}</span> },
    { key: 'solutions', header: 'Solutions', hideBelow: 'xl', render: (l) => (
      <span className="text-xs text-ink-600">{(l.solutions ?? []).join(', ') || '—'}</span>) },
    { key: 'status', header: 'Status', render: (l) => <MetaBadge meta={LEAD_STATUS} value={l.status} /> },
    { key: 'priority', header: 'Priority', sortable: true, hideBelow: 'md', render: (l) => {
      const m = PRIORITY.byValue[l.priority as Priority]; return <StatusBadge tone={m.tone}>{m.label}</StatusBadge>; } },
    { key: 'budget', header: 'Budget', numeric: true, sortable: true, hideBelow: 'md', render: (l) => (l.budget ? inr(l.budget, true) : '—') },
    { key: 'assignedTo', header: 'Owner', hideBelow: 'lg', render: (l) => l.assignedToName ?? '—' },
    { key: 'next', header: 'Next follow-up', hideBelow: 'xl', render: (l) => {
      if (!l.nextFollowUpAt) return <span className="text-ink-400">None set</span>;
      const late = new Date(l.nextFollowUpAt) < new Date();
      return <span className={late ? 'font-medium text-danger' : ''}>{dateTimeLabel(l.nextFollowUpAt)}</span>; } },
    { key: 'expectedCloseDate', header: 'Closing', sortable: true, hideBelow: 'xl', render: (l) => dateLabel(l.expectedCloseDate) },
  ];

  const filters = (
    <FilterBar active={activeFilters} onReset={reset}>
      <SearchInput value={search} onChange={(v) => reflow(() => setSearch(v))} placeholder="Company, contact, code or mobile" />
      <FilterSelect label="Status" value={status} allLabel="All statuses" onChange={(v) => reflow(() => setStatus(v))}
        options={LEAD_STATUS.list.map((s) => ({ value: s.value, label: s.label }))} />
      <FilterSelect label="Priority" value={priority} allLabel="Any priority" className="w-36" onChange={(v) => reflow(() => setPriority(v))}
        options={PRIORITY.list.map((s) => ({ value: s.value, label: s.label }))} />
      <FilterSelect label="Source" value={source} allLabel="Any source" className="w-40" onChange={(v) => reflow(() => setSource(v))}
        options={(meta.data?.sources ?? []).map((s: string) => ({ value: s, label: s }))} />
      {can('crm.lead.view.all') && (
        <FilterSelect label="Owner" value={assignedTo} allLabel="Any owner" onChange={(v) => reflow(() => setAssignedTo(v))}
          options={(meta.data?.people?.sales ?? []).map((p: any) => ({ value: p.id, label: p.fullName }))} />
      )}
      <FilterSelect label="Follow-up" value={followUp} allLabel="Any follow-up" className="w-44" onChange={(v) => reflow(() => setFollowUp(v))}
        options={[{ value: 'overdue', label: 'Overdue' }, { value: 'today', label: 'Due today' }, { value: 'none', label: 'None scheduled' }]} />
    </FilterBar>
  );

  return (
    <>
      <PageHeader title="Leads & enquiries"
        description="Every customer enquiry from first contact to the day it becomes a project."
        actions={<>
          <div className="flex rounded-lg ring-1 ring-inset ring-line-strong" role="group" aria-label="View">
            {([['list', List, 'List'], ['board', LayoutGrid, 'Board']] as const).map(([v, Icon, label]) => (
              <button key={v} onClick={() => setView(v)} aria-pressed={view === v}
                className={cx('flex h-9.5 items-center gap-1.5 px-3 text-sm font-medium first:rounded-l-lg last:rounded-r-lg',
                  view === v ? 'bg-ink-900 text-white' : 'bg-white text-ink-600 hover:bg-ink-50')}>
                <Icon className="h-4 w-4" aria-hidden />{label}
              </button>))}
          </div>
          {can('crm.lead.create') && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>New enquiry</Button>
          )}
        </>}>
        <dl className="grid grid-cols-3 gap-3 sm:max-w-xl">
          {[['Open enquiries', totals?.open], ['Qualified', totals?.qualified], ['Open budget', totals ? inr(totals.openValue, true) : undefined]].map(([label, value]) => (
            <div key={label as string} className="rounded-lg bg-sunken/60 px-3 py-2">
              <dt className="text-xs text-ink-500">{label}</dt>
              <dd className="tabular text-lg font-semibold text-ink-900">{value === undefined ? <Skeleton className="mt-1 h-5 w-10" /> : value}</dd>
            </div>))}
        </dl>
      </PageHeader>

      <div className="p-4 sm:p-6">
        {view === 'list' ? (
          <DataTable mobile="cards" columns={columns} rows={rows} rowKey={(l) => l.id}
            onRowClick={(l) => navigate(`/crm/leads/${l.id}`)}
            loading={query.isLoading} error={query.error} onRetry={() => query.refetch()}
            sort={sort} onSort={(key) => setSort((s) => ({ key: key as SortKey, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))}
            page={query.data?.page} onPage={setPage} toolbar={filters}
            empty={{ icon: <Target className="h-5 w-5" />, title: activeFilters ? 'No enquiries match these filters' : 'No enquiries yet',
              description: 'Log an enquiry as soon as a customer asks. Qualified ones become projects.',
              action: can('crm.lead.create') && !activeFilters ? <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>New enquiry</Button> : undefined }} />
        ) : (
          <div className="space-y-4">
            <Card>{filters}</Card>
            {query.isLoading ? <Skeleton className="h-64 w-full" />
              : query.isError ? <Card><ErrorState error={query.error} onRetry={() => query.refetch()} /></Card>
              : <Board rows={rows} onRequest={statusChange.request} busy={statusChange.busy} />}
            {(query.data?.page?.total ?? 0) > rows.length && (
              <p className="text-xs text-ink-500">Showing the first {rows.length} of {query.data.page.total}. Narrow the filters to see the rest.</p>
            )}
          </div>
        )}
      </div>

      <LeadFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {statusChange.modal}
    </>
  );
}

function Board({ rows, onRequest, busy }: { rows: any[]; onRequest: (l: any, s: LeadStatus) => void; busy: boolean }) {
  return (
    <div className="grid gap-3 overflow-x-auto pb-2 md:grid-cols-5">
      {LEAD_STATUS.list.map((col) => {
        const items = rows.filter((r) => r.status === col.value);
        return (
          <section key={col.value} aria-label={col.label} className="flex min-w-[240px] flex-col gap-2 rounded-card bg-sunken/60 p-2.5">
            <header className="flex items-center justify-between px-1">
              <StatusBadge tone={col.tone} title={col.description}>{col.label}</StatusBadge>
              <span className="tabular text-xs text-ink-500">{items.length}</span>
            </header>
            {items.length === 0 && <p className="px-1 py-6 text-center text-xs text-ink-400">Nothing here</p>}
            {items.map((l) => (
              <article key={l.id} className="rounded-lg bg-card p-3 shadow-card ring-1 ring-line">
                <Link to={`/crm/leads/${l.id}`} className="block">
                  <p className="truncate text-sm font-medium text-ink-900 hover:underline">{l.company}</p>
                  <p className="truncate text-xs text-ink-500">{l.contactPerson} · {l.leadCode}</p>
                </Link>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <StatusBadge tone={PRIORITY.byValue[l.priority as Priority].tone} dot={false}>{PRIORITY.byValue[l.priority as Priority].label}</StatusBadge>
                  {l.budget ? <span className="tabular text-xs font-medium text-ink-700">{inr(l.budget, true)}</span> : null}
                </div>
                <p className={cx('mt-1.5 text-xs', l.nextFollowUpAt && new Date(l.nextFollowUpAt) < new Date() ? 'text-danger' : 'text-ink-500')}>
                  {l.nextFollowUpAt ? `Follow up ${dateTimeLabel(l.nextFollowUpAt)}` : 'No follow-up set'}
                </p>
                {(LEAD_TRANSITIONS[l.status as LeadStatus] ?? []).length > 0 && (
                  <div className="mt-2 border-t border-line pt-2">
                    <label className="sr-only" htmlFor={`mv-${l.id}`}>Move {l.company}</label>
                    <Select id={`mv-${l.id}`} value="" disabled={busy} className="!h-8 text-xs"
                      onChange={(e) => e.target.value && onRequest(l, e.target.value as LeadStatus)}>
                      <option value="">Move to…</option>
                      {LEAD_TRANSITIONS[l.status as LeadStatus].map((s) => <option key={s} value={s}>{LEAD_STATUS.byValue[s].label}</option>)}
                    </Select>
                  </div>
                )}
              </article>
            ))}
          </section>
        );
      })}
    </div>
  );
}
